<template>
  <div id="app" :class="['app ' + $route.name]">
    <transition name="fade">
      <div id="page" class="page" v-if="pageLoaded">
        <transition name="fade">
          <Menu v-if="menuVisible" @scrollToSection="scrollToSection"/>
        </transition>
        <div class="mob menu-icon" @click="toggleMenu" :class="{ 'active': menuVisible }">
          <span></span>
          <span></span>
          <span></span>
        </div>
        <div class='header-container'>
          <Header :searchedProductText="searchedProductText" @openSignInModal="openSignInModal"
                  @openSignUpModal="openSignUpModal" @logout="logout" @scrollToSection="scrollToSection"/>
        </div>
        <router-view @logout="logout" @getProductPageContent="getProductPageContent" @formatDate="formatDate"
                     :currency="internalCurrencyName" @goToProduct="goToProduct" :productPageItem="productPageItem"
                     :models="models"
                     @openReadyModal="openReadyModal" @openTopUpModal="openTopUpModal"
                     @openSignInModal="openSignInModal"/>
        <div class='footer-container'>
          <Footer v-if="isAuth || (!isAuth && $route.name != 'Profile' && $route.name != 'Cart')"
                  @scrollToSection="scrollToSection"/>
        </div>
        <transition name="fade">
          <SignInModal v-if="signInModalIsVisible" @closeSignInModal="closeSignInModal"
                       @openSignUpModal="openSignUpModal" @login="login" @forgotPass="forgotPass"/>
        </transition>
        <transition name="fade">
          <SignUpModal v-if="signUpModalIsVisible" @closeSignUpModal="closeSignUpModal"
                       @openSignInModal="openSignInModal" @registration="registration"/>
        </transition>
        <transition name="fade">
          <TopUpModal v-if="topUpModalVisible" @openOrderModal="openOrderModal" :topUpAmount="topUpAmount"
                      :itemType="itemType"/>
        </transition>
        <transition name="fade">
          <OrderModal :orderData="orderData" v-show="orderModalIsVisible" @orderSubmit="orderSubmit"/>
        </transition>
        <transition name="fade">
          <ReadyModal v-if="readyModalVisible" ref="readyModal"/>
        </transition>

        <div class="modal-overlay" v-if="cookesModalIsVisible && !isOnCookiePolicyPage">
          <div class="cookies-modal">
            <div class="desc">
              {{
                $t(`We use cookies on our website to enhance your experience. Before you proceed with using our Website, please get acquainted with the way we handle a User's data and the use of Usage of`)
              }} <a href="#" @click.prevent="goToPage('cookie-policy')">{{ $t(`cookie policy`) }}</a>.
            </div>
            <button class="button" @click="acceptCookies">{{ $t(`Accept`) }}</button>
            <button class="button" @click="declineCookies">{{ $t(`Reject`) }}</button>
          </div>
        </div>
      </div>
    </transition>

    <ChatBot/>
  </div>
</template>

<script setup>
import {ref, reactive, computed, watch, onMounted, onBeforeUnmount, provide} from 'vue';
import {useStore} from 'vuex';
import {useRoute, useRouter} from 'vue-router';

import Header from './components/Header.vue';
import Menu from './components/Menu.vue';
import Footer from './components/Footer.vue';
import SignInModal from './components/SignInModal.vue';
import SignUpModal from './components/SignUpModal.vue';
import TopUpModal from './components/TopUpModal.vue';
import ReadyModal from './components/ReadyModal.vue';
import OrderModal from './components/OrderModal.vue';
import ChatBot from './components/ChatBot.vue';
import VueScrollTo from 'vue-scrollto';
import axios from "@/axios";

// Vuex store
const store = useStore();

// Vue router
const route = useRoute();
const router = useRouter();

const readyModal = ref(null)

// Reactive state variables
const pageLoaded = ref(false);
provide('pageLoaded', pageLoaded);

const menuVisible = ref(false);
provide('menuVisible', menuVisible);

const signInModalIsVisible = ref(false);
provide('signInModalIsVisible', signInModalIsVisible);

const signUpModalIsVisible = ref(false);
provide('signUpModalIsVisible', signUpModalIsVisible);

const error = ref('');
provide('error', error);

const successMessage = ref('');
provide('successMessage', successMessage);

const orderHistory = ref([]);
provide('orderHistory', orderHistory);

const promtHistory = ref([]);
provide('promtHistory', promtHistory);

const depositHistory = ref([]);
provide('depositHistory', depositHistory);

const stableDiffusion = ref([]);
provide('stableDiffusion', stableDiffusion);

const dalle2 = ref([]);
provide('dalle2', dalle2);

const chatGpt = ref([]);
provide('chatGpt', chatGpt);

const searchedProductText = ref('');
provide('searchedProductText', searchedProductText);

const totalSum = ref('0.00');
provide('totalSum', totalSum);

const textPageTitle = ref('');
provide('textPageTitle', textPageTitle);

const textPageHeader = ref('');
provide('textPageHeader', textPageHeader);

const textPageContent = ref('');
provide('textPageContent', textPageContent);

const productPageItem = ref(null);
provide('productPageItem', productPageItem);

const imgDomain = ref('');
provide('imgDomain', imgDomain);

const textPageList = ref([]);
provide('textPageList', textPageList);

const hostname = ref('');
provide('hostname', hostname);

const models = ref([]);
provide('models', models);

const categories = ref([]);
provide('categories', categories);

const categoryOptions = ref([]);
provide('categoryOptions', categoryOptions);

const modelOptions = ref([]);
provide('modelOptions', modelOptions);

const packs = ref([]);
provide('packs', packs);

const activePackProducts = ref('');
provide('activePackProducts', activePackProducts);

const activePack = ref({});
provide('activePack', activePack);

const productList = ref([]);
provide('productList', productList);

const sort = ref('');
provide('sort', sort);

const activeCategories = ref([]);
provide('activeCategories', activeCategories);

const activeModels = ref([]);
provide('activeModels', activeModels);

const dropdownCategories = ref([]);
provide('dropdownCategories', dropdownCategories);

const topUpModalVisible = ref(false);
provide('topUpModalVisible', topUpModalVisible);

const readyModalVisible = ref(false);
provide('readyModalVisible', readyModalVisible);

const readyPromt = ref(false);
provide('readyPromt', readyPromt);

const readyPack = ref(false);
provide('readyPack', readyPack);

const tranding = ref([]);
provide('tranding', tranding);

const readyProduct = ref('');
provide('readyProduct', readyProduct);

const settings = ref('');
provide('settings', settings);

const socialLinks = ref([]);
provide('socialLinks', socialLinks);

const productPageType = ref('');
provide('productPageType', productPageType);

const orderModalIsVisible = ref(false);
provide('orderModalIsVisible', orderModalIsVisible);

const orderData = ref({});
provide('orderData', orderData);

const paymentMethods = ref([]);
provide('paymentMethods', paymentMethods);
provide('availablePaymentMethods', paymentMethods);

const isValidEmail = ref(true);
provide('isValidEmail', isValidEmail);

const allPaymentMethods = ref([]);
provide('allPaymentMethods', allPaymentMethods);

const appDomain = ref('');
provide('appDomain', appDomain);

const cookesModalIsVisible = ref(false);
provide('cookesModalIsVisible', cookesModalIsVisible);

const topUpAmount = ref(0);
provide('topUpAmount', topUpAmount);

const internalCurrencyName = ref('StormBills');
provide('internalCurrencyName', internalCurrencyName);

const internalCurrencyImage = ref(require('@/images/storm-bills.png'));
provide('internalCurrencyImage', internalCurrencyImage);

const exchangeRate = ref(1);
provide('exchangeRate', exchangeRate);

const itemType = ref(null);
provide('itemType', itemType);

// Computed getters from Vuex if needed (example)
// const isAuth = computed(() => store.getters['user/isAuth']);
// But if you want to call actions/getters, you can call store.dispatch and store.getters directly

// Watchers
watch(() => route.fullPath, () => {
  window.scrollTo(0, 0);
  closeMenu();
  if (isAuth.value) {
    getOrderHistory();
    getPromtHistory();
    getDepositHistory();
  }
});

watch(() => store.state.app.currency, () => {
  getSettings();
});

// Helpers for isAuth getter
const isAuth = computed(() => store.getters['user/isAuth']);
const currency = computed(() => store.getters['app/currency']);

provide('isAuth', isAuth)
provide('currency', currency)

// Methods implementation

async function fetchCurrencies() {
  await store.dispatch('app/fetchCurrencies');
}

async function fetchLanguages() {
  await store.dispatch('app/fetchLanguages');
}

async function changeIsAuth(value) {
  await store.dispatch('user/changeIsAuth', value);
}

async function changeEmailVerified(value) {
  await store.dispatch('user/changeEmailVerified', value);
}

function cookiesModal() {
  const cookiesAccepted = localStorage.getItem("cookiesAccepted");
  if (!cookiesAccepted) {
    cookesModalIsVisible.value = true;
  }
}

function declineCookies() {
  cookesModalIsVisible.value = false;
}

function acceptCookies() {
  localStorage.setItem("cookiesAccepted", "true");
  cookesModalIsVisible.value = false;
}

function clearError() {
  error.value = '';
}

provide('clearError', clearError)

function closeMenu() {
  menuVisible.value = false;
  document.body.style.overflow = "auto";
}

function toggleMenu() {
  menuVisible.value = !menuVisible.value;
  window.scrollTo(0, 0);
  if (menuVisible.value) {
    document.body.style.overflow = "hidden";
  } else {
    document.body.style.overflow = "auto";
  }
}

function menuHeight() {
  const doc = document.documentElement;
  doc.style.setProperty("--vh", `${window.innerHeight * 0.01}px`);
}

function clearDropdownCategories() {
  dropdownCategories.value = [];
}

function openModelDropdown(model) {
  dropdownCategories.value = categories.value.filter(item => item.parent_id === model.id);
}

function getDomainName(hostName) {
  return hostName.substring(hostName.lastIndexOf(".", hostName.lastIndexOf(".") - 1) + 1);
}

function openSignInModal() {
  closeMenu();
  clearError();
  signUpModalIsVisible.value = false;
  signInModalIsVisible.value = true;
}

provide('openSignInModal', openSignInModal)

function openSignUpModal() {
  clearError();
  signInModalIsVisible.value = false;
  signUpModalIsVisible.value = true;
}

function closeSignUpModal() {
  signUpModalIsVisible.value = false;
  clearError();
}

function closeSignInModal() {
  signInModalIsVisible.value = false;
  clearError();
}

async function fetchCSRFToken() {
  try {
    await axios.get('sanctum/csrf-cookie', {baseURL: process.env.VUE_APP_DOMAIN});
  } catch (error) {
    console.error("Failed to fetch CSRF token:", error);
  }
}

async function login(data) {
  try {
    await fetchCSRFToken();
    const res = await axios.post('login', data);
    if (res.data.status == "OK") {
      await changeIsAuth(true);
      await changeEmailVerified(!!res.data.payload.email_verified);
      closeSignInModal();
      getOrderHistory();
      getPromtHistory();
      getDepositHistory();
    } else {
      error.value = res.data.message;
    }
  } catch (err) {
    if (err.response) {
      error.value = err.response.data.message;
    } else {
      error.value = "An error occurred during login.";
    }
  }
}

function forgotPass(data) {
  axios.post('user/reset-password', data)
      .then((res) => {
        if (res.data.status == "OK") {
          successMessage.value = res.data.message;
          error.value = ''
          setTimeout(() => {
            successMessage.value = '';
          }, 2000)
        } else {
          successMessage.value = '';
          error.value = res.response.data.message
        }
      })
      .catch((errorResp) => {
        error.value = errorResp.response?.data?.message || errorResp.message || 'An error occurred'
      })
}

function registration(data) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  isValidEmail.value = emailRegex.test(data.email);

  if (data.password !== data.passConfirm) {
    error.value = 'Password confirmation failed!';
  } else if (!isValidEmail.value) {
    error.value = 'Email format failed!';
  } else {
    axios.post('register', data)
        .then(async (res) => {
          if (res.data.status == "OK") {
            clearError();
            await changeIsAuth(true);
            closeSignUpModal();
          } else {
            error.value = res.data.message;
          }
        })
        .catch((res) => {
          error.value = res.response?.data?.message || res.message || 'An error occurred';
        })
  }

  console.log(error.value)
}

function logout() {
  axios.post('logout')
      .then(async () => {
        if (route.name != 'Home') {
          router.push({path: '/'});
        }
        await changeIsAuth(false);
      })
      .catch((res) => {
        if (res.response?.status === 401) {
          openSignInModal();
        }
      })
}

provide('logout', logout)

function getOrderHistory() {
  orderHistory.value = [];
  axios.get('purchase/history')
      .then((res) => {
        orderHistory.value = res.data.payload;
      })
      .catch((res) => {
        if (res.response?.status === 401) {
          openSignInModal();
        }
      })
}

function getPromtHistory() {
  promtHistory.value = [];
  axios.get('user/prompts')
      .then((res) => {
        promtHistory.value = res.data.payload;
      })
      .catch((res) => {
        if (res.response?.status === 401) {
          openSignInModal();
        }
      })
}

function getDepositHistory() {
  depositHistory.value = [];
  axios.get('deposit/history')
      .then((res) => {
        depositHistory.value = res.data.payload;
      })
      .catch((res) => {
        if (res.response?.status === 401) {
          openSignInModal();
        }
      })
}

function formatDate(num) {
  const objectDate = new Date(num);
  let day = objectDate.getDate();
  if (day < 10) day = '0' + day;
  let month = objectDate.getMonth() + 1;
  if (month < 10) month = '0' + month;
  let year = objectDate.getFullYear();
  return `${day}.${month}.${year}`;
}

provide('formatDate', formatDate)

function formatPrice(num) {
  return num.toLocaleString('ru-RU', {minimumFractionDigits: 2});
}

async function getSocialLinks() {
  axios.get('social-links')
      .then(({ data }) => {
        socialLinks.value = data.payload;
      })
      .catch((error) => {
        console.log(error);
      });
}

async function getSettings() {
  try {
    const res = await axios.get('settings');
    if (res.data.status === "OK") {
      settings.value = res.data.payload;
      internalCurrencyName.value = settings.value.internal_currency_name;
      internalCurrencyImage.value = settings.value.internal_currency_image;
      exchangeRate.value = settings.value.exchange_rate;
      appDomain.value = getDomainName(window.location.hostname);
    }
  } catch (err) {
    console.error(err);
  }
}

async function getModels() {
  try {
    const res = await axios.get('models');
    models.value = res.data.payload;
    modelOptions.value = models.value.map(i => ({value: i.id, text: i.name}));
  } catch (err) {
    console.error('Failed to load models:', err.message);
    // Set default empty values to prevent app from breaking
    models.value = [];
    modelOptions.value = [];
  }
}

async function getCategories() {
  try {
    const res = await axios.get('categories');
    categories.value = res.data.payload;
    categoryOptions.value = categories.value.map(i => ({value: i.id, text: i.name}));
  } catch (err) {
    console.error('Failed to load categories:', err.message);
    // Set default empty values to prevent app from breaking
    categories.value = [];
    categoryOptions.value = [];
  }
}

async function getPacks() {
  try {
    const res = await axios.get('packs');
    packs.value = res.data.payload;
  } catch (err) {
    console.error(err);
  }
}

provide('getPacks', getPacks)

async function getTranding() {
  tranding.value = [];
  axios.get('products/trending')
      .then((res) => {
        tranding.value = res.data.payload
      })
      .catch(() => {

      })
}

provide('getTranding', getTranding)

async function getProducts(packId) {
  try {
    const res = await axios.get(`products?pack_id=${packId}`);
    productList.value = res.data.payload;
  } catch (err) {
    console.error(err);
  }
}

function openTopUpModal(amount = 0.0, type = null) {
  topUpAmount.value = amount;
  itemType.value = type;
  topUpModalVisible.value = true;
}

provide('openTopUpModal', openTopUpModal)

function closeTopUpModal() {
  topUpModalVisible.value = false;
}

provide('closeTopUpModal', closeTopUpModal)

function openReadyModal(itemType = null, product = null) {
  // Reset states
  readyPromt.value = false;
  readyPack.value = false;
  successMessage.value = '';  // Clear any previous success message
  error.value = '';           // Clear any previous error message
  
  // Set the appropriate state based on the item type or product
  if (itemType === 'pack' || (product && product.product_type === 'pack')) {
    readyPack.value = true;
  } else if (itemType === 'prompt' || (product && product.product_type === 'prompt')) {
    readyPromt.value = true;
  } else {
    // Fallback: determine from productPageType or productPageItem
    if (productPageType.value === 'pack' || (productPageItem.value && productPageItem.value.product_type === 'pack')) {
      readyPack.value = true;
    } else {
      readyPromt.value = true;
    }
  }
  
  readyModalVisible.value = true;
}

provide('openReadyModal', openReadyModal)

function closeReadyModal() {
  readyModalVisible.value = false;
}

provide('closeReadyModal', closeReadyModal)

function openOrderModal(data) {
  if (data && data.orderData && data.paymentMethods) {
    orderData.value = data.orderData;
    paymentMethods.value = data.paymentMethods;
  }
  orderModalIsVisible.value = true;
}

function closeOrderModal() {
  orderModalIsVisible.value = false;
}

provide('closeOrderModal', closeOrderModal)

function orderSubmit(data, type) {
  data.paymentType = type;
  const apiUrl = data.itemType ? 'deposit/bundle-checkout' : 'deposit/checkout';
  
  axios.post(apiUrl, data)
    .then((res) => {
      if (res.data.status == 'ERROR') {
        // Handle error - redirect to payment fail page
        orderModalIsVisible.value = false;
        // Note: You might need to implement router navigation here if needed
        console.error('Payment failed:', res.data);
      } else {
        window.location = res.data.redirect_url;
      }
    })
    .catch((res) => {
      // Handle API error
      console.error('API Error:', res.response?.data?.message);
      // Note: You might want to implement toast notifications here
    });
}

async function buyProduct(promptId) {
  try {
    const data = { prompt_id: promptId };
    const res = await axios.post('purchase/prompt', data, {timeout: 60000});
    if (res.data.status === "OK") {
      closeReadyModal();
      successMessage.value = 'Purchase successful!';
      getOrderHistory();
      getPromtHistory();
      router.push({ path: '/profile/order-history' });
    } else {
      error.value = res.data.message;
    }
  } catch (err) {
    error.value = err.response?.data?.message || "Purchase failed";
  }
}

async function buyPack(packId = null) {
  try {
    // Use the provided packId or fall back to productPageItem or activePack
    const id = packId || productPageItem.value?.id || activePack.value?.id;
    
    if (!id) {
      error.value = 'Pack ID not found';
      return;
    }

    const data = { pack_id: id };
    const res = await axios.post('purchase/pack', data, { timeout: 60000 });
    
    if (res.data.status === "OK") {
      closeReadyModal();
      successMessage.value = 'Purchase successful!';
      getOrderHistory();
      getPromtHistory();
      router.push({ path: '/profile/order-history' });
    } else {
      error.value = res.data.message;
    }
  } catch (err) {
    error.value = err.response?.data?.message || err.message || 'Purchase failed';
  } finally {
    if (readyModal.value) {
      readyModal.value.isLoading = false;
    }
  }
}

provide('buyProduct', buyProduct)
provide('buyPack', buyPack)

function scrollToSection(name) {
  VueScrollTo.scrollTo(name, {offset: -70, duration: 500});
}

function scrollToTop() {
  window.scrollTo({top: 0, behavior: 'smooth'});
}

function selectCategory(categoryId) {
  if (activeCategories.value.includes(categoryId)) {
    activeCategories.value = activeCategories.value.filter(id => id !== categoryId);
  } else {
    activeCategories.value.push(categoryId);
  }
}

function selectModel(modelId) {
  if (activeModels.value.includes(modelId)) {
    activeModels.value = activeModels.value.filter(id => id !== modelId);
  } else {
    activeModels.value.push(modelId);
  }
}

function clearFilters() {
  activeCategories.value = [];
  activeModels.value = [];
}

function goToProduct(item) {
  productPageType.value = '';
  router.push({path: '/products/' + item.product_type + '/' + item.id, params: {data: item.id}});
  getProductPageContent(item.id);
  productPageType.value = item.product_type
}

function getProductPageContent(id, type) {
  productPageItem.value = '';

  if (type) {
    if (type == 'pack') {
      axios.get('packs/' + id + '/info')
          .then((res) => {
            productPageItem.value = res.data.payload;
          })
          .catch((res) => {
            if (res.response?.status === 401) {
              openSignInModal();
            }
          })
    } else {
      axios.get('products/' + id + '/info')
          .then((res) => {
            productPageItem.value = res.data.payload;
          })
          .catch(() => {
          })
    }
  }
}

function getTextPages() {
  return axios.get('static-pages')
      .then((res) => {
        textPageList.value = res.data.payload
      })
      .catch(() => {})
}

function goToPage(item) {
  let newItem;
  if (item == 'privacy') {
    for (let i = 0; i < textPageList.value.length; i++) {
      if (textPageList.value[i].title.toLowerCase().includes('privacy')) {
        newItem = textPageList.value[i].id;
      }
    }
  } else if (item == 'terms') {
    for (let i = 0; i < textPageList.value.length; i++) {
      if (textPageList.value[i].title.toLowerCase().includes('terms')) {
        newItem = textPageList.value[i].id;
      }
    }
  } else if (item === 'cookie-policy') {
    for (let i = 0; i < textPageList.value.length; i++) {
      if (textPageList.value[i].title.toLowerCase().includes('cookie')) {
        newItem = textPageList.value[i].id;
        break;
      }
    }
  } else {
    newItem = item
  }

  router.push({path: '/pages/' + newItem, params: {data: newItem}})
  getTextPageContent(newItem);
  closeSignUpModal();
}

provide('goToPage', goToPage)

function getTextPageContent(id) {
  textPageTitle.value = '';
  textPageHeader.value = '';
  textPageContent.value = ''

  axios.get('static-pages/' + id)
      .then((res) => {
        textPageTitle.value = res.data.payload.title;
        textPageContent.value = res.data.payload.content;
        textPageHeader.value = res.data.payload.header;
      })
      .catch((res) => {
        if (res.response?.status === 401) {
          openSignInModal();
        }
      })
}

provide('getTextPageContent', getTextPageContent)

// On mounted lifecycle
onMounted(() => {
  cookiesModal();
  menuHeight();
  getTextPages();
  fetchCurrencies();
  fetchLanguages();
  getSettings();
  // getModels();
  getCategories();
  getPacks();
  getSocialLinks()

  if (isAuth.value) {
    getOrderHistory();
    getPromtHistory();
    getDepositHistory();
  }
  pageLoaded.value = true;
  window.addEventListener('resize', menuHeight);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', menuHeight);
});
</script>

